import {
  ImagesSection,
  PriceAndBrandSection,
  ProductSelectionSection,
  ProminentBannerSection,
  SizesSection,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {
  DropDownProps,
  Dropdown as DropdownBase,
  DropdownBodyAlignment,
  ModalDialog,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {Fragment, memo, useState} from 'react';
import {MiniArticleDetailsLayout} from '../../../../features/MiniArticleDetailsLayout/MiniArticleDetailsLayout';
import {useSelectedSizeState} from '../../../../features/SizeSelection/hooks/useSelectedSizeState';
import {SizeSelectionOpenedStateKey} from '../../../../features/SizeSelectionFlyout/hooks/useSizeSelectionOpenedState';
import {AddToBasketSectionView} from '../../AddToBasketSection/AddToBasketSectionView';
import {MoreDetails} from '../../AddToBasketSection/MoreDetails/MoreDetails';
import {SoldOutSizeReminderFlyout} from '../../AddToBasketSection/SoldOutSizeReminderFlyout';
import {PriceAndBrandView} from '../../PriceAndBrand/PriceAndBrandView';
import {ProductSelectionView} from '../../ProductSelection/ProductSelectionView';
import {ProductTileImageSliderView} from '../../ProductTileImageSlider/ProductTileImageSliderView';

interface IMiniArticleDetailProps {
  imagesSection: ImagesSection;
  priceAndBrandSection: PriceAndBrandSection;
  prominentBannerSection?: ProminentBannerSection;
  productSelectionSection: ProductSelectionSection;
  sizesSection: SizesSection;
  SizeSelectionStateManagerKey: SizeSelectionOpenedStateKey;
  onAddToBasketSuccess: () => void;
}

const MemoPriceAndBrandView = memo(PriceAndBrandView);
const MemoProductSelectionView = memo(ProductSelectionView);
const MemoAddToBasketSectionView = memo(AddToBasketSectionView);
const MemoProductTileImageSliderView = memo(ProductTileImageSliderView);
const MemoSoldOutSizeReminderFlyout = memo(SoldOutSizeReminderFlyout);

export const MiniArticleDetail = ({
  imagesSection,
  priceAndBrandSection,
  productSelectionSection,
  sizesSection,
  onAddToBasketSuccess,
  SizeSelectionStateManagerKey,
}: IMiniArticleDetailProps) => {
  const {selectedSize, setSelectedSize} = useSelectedSizeState();
  const [showSoldOutSizeReminder, setShowSoldOutSizeReminder] = useState(false);

  return (
    <MiniArticleDetailsLayout
      imageSlider={
        <MemoProductTileImageSliderView
          {...imagesSection}
          showThumbnails
          selectedSizeId={selectedSize?.sizeId}
          redirectOnClick
          isInMiniADPDialog={false}
        />
      }
      priceAndBrand={
        <StyledMemoPriceAndBrandView
          {...priceAndBrandSection}
          brandLogoAlignment="left"
          selectedSize={selectedSize}
          isInMiniADPDialog
          PriceLegalInfoLayoutComponent={ModalDialog}
        />
      }
      prominentBanner={null}
      productSelection={
        <>
          <StyledProductSelectionView
            {...productSelectionSection}
            disablePreviewImage
            uniqueKey="productSelectionViewMiniADP"
            ColorSelectionLayoutComponent={Dropdown}
            onSoldOutButtonClick={() => {
              setShowSoldOutSizeReminder(true);
            }}
          />
          <MemoSoldOutSizeReminderFlyout
            setIsOpenSizeReminder={setShowSoldOutSizeReminder}
            sizeSelectionStateManagerKey={
              SizeSelectionOpenedStateKey.NO_SIZE_SELECTED_SELECTION
            }
            isOpenSizeReminder={showSoldOutSizeReminder}
          />
        </>
      }
      sizeSelection={
        <Fragment>
          <MemoAddToBasketSectionView
            {...sizesSection}
            selectedSize={selectedSize}
            setSelectedSize={setSelectedSize}
            showPriceInSizeSelectionHeader={false}
            showSizeAdvisor
            SizeReminderLayoutComponent={ModalDialog}
            SizeSelectionLayoutComponent={Dropdown}
            showWishlistButton={false}
            onAddToBasketSuccess={onAddToBasketSuccess}
            sizeSelectionStateManagerKey={SizeSelectionStateManagerKey}
            isInMiniADPDialog
          />
          <MoreDetails product={sizesSection.product} />
        </Fragment>
      }
    />
  );
};

const Dropdown: React.FC<DropDownProps> = props => (
  <StyledDropdown
    {...props}
    animationDuration={0}
    dropdownBodyAlign={DropdownBodyAlignment.LEFT}
    dropdownBodyWidth="350px"
    isActive
  />
);

const StyledDropdown = styled(DropdownBase)`
  display: flow-root;
`;

const StyledMemoPriceAndBrandView = styled(MemoPriceAndBrandView)`
  margin-bottom: 0.75rem;
`;

const StyledProductSelectionView = styled(MemoProductSelectionView)`
  margin: 0;
`;
